import { supabase } from '@/lib/supabase';

// 后台所有数据访问都走后端函数(Edge Function)，
// 由后端函数验证管理员身份后用高权限读取，前端不会直接拿到别人的敏感数据。

// ===== 独立后台登录（账号密码） =====
// 密码已迁移到 Supabase 后端（admin_credentials 表，哈希存储），
// 登录校验与修改密码均通过 admin-auth Edge Function 完成。
const ADMIN_ACCOUNT = 'admin';
const SESSION_KEY = 'jinan_admin_session';
const SESSION_TIME_KEY = 'jinan_admin_login_at';
// 后端函数会校验这个令牌（同时支持从后端 Secrets 覆盖），前后端需保持一致
const ADMIN_AUTH_TOKEN = 'jinan-admin-backend-token-2026';
// 24小时（毫秒）
const ADMIN_SESSION_TTL_MS = 24 * 60 * 60 * 1000;

export async function checkAdminCredentials(account: string, password: string): Promise<boolean> {
  if (account !== ADMIN_ACCOUNT) return false;
  try {
    const { data, error } = await supabase.functions.invoke('admin-auth', {
      body: { action: 'login', account, password },
    });
    if (error) return false;
    const result = (data ?? {}) as { data?: { ok?: boolean } };
    return !!result.data?.ok;
  } catch {
    return false;
  }
}

export function isAdminLoggedIn(): boolean {
  try {
    const token = localStorage.getItem(SESSION_KEY);
    if (token !== ADMIN_AUTH_TOKEN) return false;
    const loginAt = Number(localStorage.getItem(SESSION_TIME_KEY) || '0');
    if (!loginAt) return true; // 老版本无时间戳，保持兼容
    return Date.now() - loginAt <= ADMIN_SESSION_TTL_MS;
  } catch {
    return false;
  }
}

export function setAdminSession(): void {
  try {
    localStorage.setItem(SESSION_KEY, ADMIN_AUTH_TOKEN);
    localStorage.setItem(SESSION_TIME_KEY, String(Date.now()));
  } catch {
    // ignore
  }
}

export function clearAdminSession(): void {
  try {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(SESSION_TIME_KEY);
  } catch {
    // ignore
  }
}

export interface AdminUser {
  id: string;
  phone: string;
  name: string;
  id_card: string;
  member_level: string;
  verified: boolean;
  balance: number;
  asset_value: number;
  holding_gold: number;
  order_count: number;
  role: string;
  email: string;
  created_at: string;
}

export interface AdminOrder {
  id: string;
  user_id: string;
  name: string;
  weight: number;
  direction: string;
  open_price: number;
  current_price: number;
  close_price: number | null;
  profit: number;
  original_margin: number;
  status: string;
  create_time: string;
  close_time: string | null;
  phone: string;
  user_name: string;
  lock_expire_at: string | null;
  created_at: string | null;
}

export interface Notice {
  id: number;
  tag: string;
  title: string;
  summary: string;
  date: string;
  show_on_marquee: boolean;
}

export interface DepositRecord {
  id: number;
  user_id: string;
  amount: number;
  channel: string;
  channel_id: number | null;
  channel_receiver: string | null;
  channel_bank: string | null;
  chain: string | null;
  account: string | null;
  payer_name: string | null;
  bank_name: string | null;
  status: string;
  remark: string | null;
  create_time: string | null;
  created_at: string | null;
  phone: string;
  name: string;
  screenshots: string[];
}

export interface WithdrawRecord {
  id: number;
  user_id: string;
  asset_id: string | null;
  product: string;
  weight: number;
  method: string | null;
  amount: number;
  status: string;
  create_time: string | null;
  phone: string;
  name: string;
  channel_detail: string | null;
}

export interface PickupRecord {
  id: string;
  user_id: string;
  asset_ids: string[];
  product: string;
  total_weight: number;
  method: string;
  status: string;
  address_detail: string | null;
  logistics_no: string | null;
  fee: number;
  create_time: string | null;
  created_at: string | null;
  approved_time: string | null;
  approved_by: string | null;
  remark: string | null;
  phone: string;
}

export interface DashboardStats {
  total_users: number;
  admin_users: number;
  total_orders: number;
  open_orders: number;
  pending_deposits: number;
  pending_withdrawals: number;
  pending_pickups: number;
  total_balance: number;
  total_deposit_amount: number;
  holding_gold: number;
  holding_silver: number;
  holding_kgbar: number;
  total_holding_weight: number;
  position_detail?: PositionDetail[];
}

export interface PositionDetail {
  name: string;
  buy: number;
  sell: number;
  net: number;
}

export interface UserPosition {
  id: string;
  name: string;
  weight: number;
  direction: string;
  open_price: number;
  current_price: number;
  original_margin: number;
  profit: number;
  live_profit: number;
  create_time: string;
}

export interface UserPhysicalAsset {
  id: string;
  product: string;
  weight: number;
  status: string;
  store_date: string;
  location: string;
  created_at: string;
}

export interface UserDetail {
  profile: {
    id: string;
    phone: string;
    name: string;
    email: string;
    member_level: string;
    verified: boolean;
    balance: number;
    asset_value: number;
    role: string;
    created_at: string;
  };
  positions: UserPosition[];
  physical_assets: UserPhysicalAsset[];
  summary: {
    total_position_weight: number;
    total_floating_profit: number;
    total_margin: number;
    physical_weight: number;
    net_asset_value: number;
    total_deposit_amount: number;
    total_withdraw_amount: number;
    total_profit: number;
  };
}

export interface DepositChannel {
  id: number;
  channel_type: string;
  bank_name: string | null;
  account_name: string | null;
  account_no: string | null;
  chain_type: string | null;
  remark: string | null;
  enabled: boolean;
  sort: number;
  exchange_rate: number | null;
  created_at: string | null;
}

interface FunctionResult<T> {
  data?: T;
  error?: string;
}

async function extractServerMessage(error: unknown): Promise<string> {
  const ctx = (error as { context?: unknown }).context;
  if (ctx instanceof Response) {
    try {
      const text = await ctx.text();
      if (text) {
        try {
          const parsed = JSON.parse(text) as Record<string, unknown>;
          return (parsed.error as string) || (parsed.message as string) || text;
        } catch {
          return text;
        }
      }
    } catch {
      // ignore
    }
  } else if (ctx && typeof ctx === 'object') {
    const c = ctx as Record<string, unknown>;
    return (c.error as string) || (c.message as string) || '';
  } else if (typeof ctx === 'string') {
    try {
      const parsed = JSON.parse(ctx) as Record<string, unknown>;
      return (parsed.error as string) || (parsed.message as string) || ctx;
    } catch {
      return ctx;
    }
  }
  return '';
}

async function adminRequest<T>(fn: string, body: Record<string, unknown> = {}): Promise<T> {
  const { data, error } = await supabase.functions.invoke(fn, {
    body,
    headers: {
      Authorization: `Bearer ${ADMIN_AUTH_TOKEN}`,
      'x-admin-token': ADMIN_AUTH_TOKEN,
    },
  });
  if (error) {
    const serverMsg = await extractServerMessage(error);
    throw new Error(serverMsg || error.message || '请求失败');
  }
  const result = (data ?? {}) as FunctionResult<T>;
  if (result.error) throw new Error(result.error);
  return result.data as T;
}

export const adminApi = {
  changeAdminPassword: (oldPassword: string, newPassword: string) =>
    adminRequest<{ ok: boolean }>('admin-auth', { action: 'change_password', old_password: oldPassword, new_password: newPassword }),

  dashboard: () =>
    adminRequest<DashboardStats>('admin-dashboard'),

  listUsers: () =>
    adminRequest<AdminUser[]>('admin-users', { action: 'list' }),

  clearAllData: () =>
    adminRequest<{ ok: boolean; kept_admin_profiles?: number; deleted_auth_users?: number; results?: Record<string, { success: boolean; error?: string }> }>('clear-all-data', { action: 'clear_all', confirm: 'CLEAR_ALL' }),

  getUserDetail: (userId: string) =>
    adminRequest<UserDetail>('admin-users', { action: 'get_user_detail', user_id: userId }),

  setUserRole: (userId: string, role: string) =>
    adminRequest<{ ok: boolean }>('admin-users', { action: 'set_role', user_id: userId, role }),

  deleteUser: (userId: string) =>
    adminRequest<{ ok: boolean; results?: Record<string, { success: boolean; error?: string }> }>('admin-delete-user', { user_id: userId }),

  listOrders: () =>
    adminRequest<AdminOrder[]>('admin-orders', { action: 'list' }),

  listNotices: () =>
    adminRequest<Notice[]>('admin-notices', { action: 'list' }),

  createNotice: (payload: { tag: string; title: string; summary: string; date: string; show_on_marquee?: boolean }) =>
    adminRequest<{ ok: boolean }>('admin-notices', { action: 'create', ...payload }),

  updateNotice: (payload: { id: number; tag: string; title: string; summary: string; date: string; show_on_marquee?: boolean }) =>
    adminRequest<{ ok: boolean }>('admin-notices', { action: 'update', ...payload }),

  deleteNotice: (id: number) =>
    adminRequest<{ ok: boolean }>('admin-notices', { action: 'delete', id }),

  listDeposits: () =>
    adminRequest<DepositRecord[]>('admin-deposits', { action: 'list' }),

  confirmDeposit: (id: number, amount?: number) =>
    adminRequest<{ ok: boolean }>('admin-deposits', { action: 'confirm', id, amount }),

  rejectDeposit: (id: number) =>
    adminRequest<{ ok: boolean }>('admin-deposits', { action: 'reject', id }),

  listWithdrawals: () =>
    adminRequest<WithdrawRecord[]>('admin-withdrawals', { action: 'list' }),

  approveWithdrawal: (id: number) =>
    adminRequest<{ ok: boolean }>('admin-withdrawals', { action: 'approve', id }),

  rejectWithdrawal: (id: number) =>
    adminRequest<{ ok: boolean }>('admin-withdrawals', { action: 'reject', id }),

  listPickups: () =>
    adminRequest<PickupRecord[]>('admin-pickups', { action: 'list' }),

  approvePickup: (id: string, opts?: { logistics_no?: string; fee?: number; remark?: string }) =>
    adminRequest<{ ok: boolean }>('admin-pickups', { action: 'approve', id, ...opts }),

  rejectPickup: (id: string, remark?: string) =>
    adminRequest<{ ok: boolean }>('admin-pickups', { action: 'reject', id, remark }),

  listChannels: () =>
    adminRequest<DepositChannel[]>('admin-channels', { action: 'list' }),

  createChannel: (payload: {
    channel_type: string;
    bank_name?: string;
    account_name: string;
    account_no: string;
    chain_type?: string;
    remark?: string;
    enabled: boolean;
    sort?: number;
    exchange_rate?: number | null;
  }) => adminRequest<{ ok: boolean }>('admin-channels', { action: 'create', ...payload }),

  updateChannel: (payload: Partial<DepositChannel> & { id: number }) =>
    adminRequest<{ ok: boolean }>('admin-channels', { action: 'update', ...payload }),

  deleteChannel: (id: number) =>
    adminRequest<{ ok: boolean }>('admin-channels', { action: 'delete', id }),

  getSchedule: () =>
    adminRequest<{ id: number; market_name: string; open_time: string; close_time: string; timezone: string; weekend_enabled: boolean; enabled: boolean; manual_paused: boolean; pause_reason: string | null; paused_categories: string[] | null; updated_at: string }>('admin-trading-schedule', { action: 'get' }),

  updateSchedule: (payload: {
    open_time?: string;
    close_time?: string;
    timezone?: string;
    weekend_enabled?: boolean;
    enabled?: boolean;
  }) => adminRequest<{ ok: boolean }>('admin-trading-schedule', { action: 'update', ...payload }),

  setPause: (paused: boolean, reason?: string) =>
    adminRequest<{ ok: boolean; paused: boolean }>('admin-trading-schedule', { action: 'set_pause', paused, reason }),

  toggleCategory: (category: string, paused: boolean) =>
    adminRequest<{ ok: boolean; category: string; paused: boolean; pausedCategories: string[] }>('admin-trading-schedule', { action: 'toggle_category', category, paused }),
};